export type PaymentElementLoadStatus = "loading" | "ready" | "error";

export type PaymentElementLoadEvent =
	| "ready"
	| "load_error"
	| "timeout"
	| "retry";

export const PAYMENT_ELEMENT_LOAD_TIMEOUT_MS = 15_000;

/**
 * Reduce PaymentElement load UI state from ready / load-error / timeout /
 * retry events.
 *
 * Late timeout or load_error after ready must not flip the form back to an
 * error — Stripe can fire out of order relative to our watchdog timer.
 *
 * @param status - Current load status
 * @param event - Lifecycle event from PaymentElement or the watchdog
 * @returns Next load status
 */
export const reducePaymentElementLoadState = (
	status: PaymentElementLoadStatus,
	event: PaymentElementLoadEvent,
): PaymentElementLoadStatus => {
	switch (event) {
		case "ready":
			return "ready";
		case "load_error":
		case "timeout":
			return status === "ready" ? "ready" : "error";
		case "retry":
			return "loading";
	}
};

/**
 * Whether the PaymentElement load watchdog should be counting.
 *
 * On slow mobile networks Stripe.js itself can take most of the budget; the
 * timeout must only measure PaymentElement, not the script download.
 *
 * @param options.isCardFormShown - New-card form is on screen
 * @param options.isStripeReady - Stripe.js and Elements are available
 * @returns True when the timeout should run
 */
export const shouldRunPaymentElementWatchdog = (options: {
	isCardFormShown: boolean;
	isStripeReady: boolean;
}): boolean => options.isCardFormShown && options.isStripeReady;

/**
 * PostHog `checkout_blocked` error_code for PaymentElement load failures.
 *
 * @param event - load_error or timeout
 * @returns Dedicated blocked code for analytics
 */
export const getPaymentElementLoadBlockedCode = (
	event: "load_error" | "timeout",
): "payment_element_load_error" | "payment_element_load_timeout" => {
	return event === "timeout"
		? "payment_element_load_timeout"
		: "payment_element_load_error";
};

export type PaymentElementLoadError = {
	type?: string;
	code?: string;
	message?: string;
};

export type PaymentElementLoadErrorDetails = {
	stripe_error_type?: string;
	stripe_error_code?: string;
	stripe_error_message?: string;
};

const STRIPE_ERROR_MESSAGE_MAX_LENGTH = 300;

/**
 * PostHog properties for the error Stripe passes to `onLoadError`.
 *
 * @param error - Stripe's load error; undefined for watchdog timeouts
 * @returns Sparse `stripe_error_*` properties
 */
export const getPaymentElementLoadErrorDetails = (
	error: PaymentElementLoadError | undefined,
): PaymentElementLoadErrorDetails => {
	if (!error) return {};
	return {
		stripe_error_type: error.type,
		stripe_error_code: error.code,
		stripe_error_message: error.message?.slice(
			0,
			STRIPE_ERROR_MESSAGE_MAX_LENGTH,
		),
	};
};

/**
 * Short user-facing copy when the card form never becomes ready.
 *
 * @param options.canUsePayPal - PayPal is already offered on this surface
 * @returns Error message for the load-failure UI
 */
export const getPaymentElementLoadErrorMessage = (options: {
	canUsePayPal?: boolean;
}): string => {
	if (options.canUsePayPal) {
		return "Card form failed to load. Try again, or use PayPal instead.";
	}

	return "Card form failed to load. Please try again.";
};
